import { useField } from "formik";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Tooltip from "react-tooltip-lite";
import { Icon } from "../../Icon/Icon";
import { PasswordErrorMessageTooltip } from "../PasswordErrorMessageTooltip/PasswordErrorMessageTooltip";
import { Select, SelectOption } from "../Select/Select";
import "./EditableInformation.css";

export /**
 *
 * @param {*} props
 * @prop {ReactNode|ReactNode[]} props.children
 * @prop {string} props.title
 * @return {*}
 */
const EditableInformation = ({ children, title }) => {
  const { t } = useTranslation();
  return (
    <div className="EditableInformation">
      <div className="EditableInformation__title">
        <div className="EditableInformation__title-text">{t(title)}</div>
      </div>
      {children}
    </div>
  );
};

export const EditableInformationTextDisplay = ({ value }) => {
  return (
    <div className="EditableInformationTextDisplay">
      <div className="EditableInformationTextDisplay__text">{value}</div>
    </div>
  );
};

export const EditableInformationStatusDisplay = ({ value, withSquare }) => {
  const { t } = useTranslation();
  return (
    <div className="EditableInformationStatusDisplay">
      {withSquare && (
        <div
          className="EditableInformationStatusDisplay__blob"
          style={{ background: value === "Suspended" ? "#EAC75A" : "#56B267" }}
        ></div>
      )}
      <div className="EditableInformationStatusDisplay__text">{t(value)}</div>
    </div>
  );
};

/**
 *
 * @param {object} props
 * @prop {label} props.label
 * @prop {boolean} props.defaultEditingState
 * @prop {function} props.onBlur
 * @prop {boolean} canEdit
 * @return {*}
 */
export const EditableInformationTextInput = ({
  label = "",
  defaultEditingState = false,
  onBlur = () => {},
  canEdit = true,
  ...props
}) => {
  const [field, meta] = useField(props);
  const [hasFocus, setHasFocus] = useState(false);
  const [editing, setEditing] = useState(defaultEditingState);
  const { t } = useTranslation();

  return (
    <div className="EditableInformationTextInput">
      {!editing && <EditableInformationTextDisplay value={field.value} />}
      {canEdit && editing && (
        <input
          {...field}
          {...props}
          className="EditableInformationTextInput__field"
          onFocusCapture={() => setHasFocus(true)}
          onBlur={(args) => {
            setHasFocus(false);
            field.onBlur(args);
            onBlur();
          }}
        />
      )}
      {label && (
        <label
          className={
            hasFocus || meta.value !== ""
              ? "EditableInformationTextInput__label EditableInformationTextInput__label--focused"
              : "EditableInformationTextInput__label"
          }
        >
          {label}
        </label>
      )}
      {meta.touched && meta.error ? (
        <div className="EditableInformationTextInput__error">
          <Tooltip
            padding={0}
            content={
              <PasswordErrorMessageTooltip>
                {t(meta.error)}
              </PasswordErrorMessageTooltip>
            }
          >
            <Icon name="iconNotification" width={20} height={20} />
          </Tooltip>
        </div>
      ) : null}
      {canEdit && !defaultEditingState && (
        <div className="EditableInformationTextInput__button">
          <div
            className="EditableInformationTextInput__button-toggle"
            onClick={() => setEditing(!editing)}
          >
            {!defaultEditingState && (
              <Icon
                name={editing ? "iconCheckmark" : "iconEdit"}
                width={15}
                height={18}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 *
 * @param {*} props
 * @prop {string} label
 * @prop {array} options
 * @prop {boolean} defaultEditingState
 * @prop {boolean} canEdit
 * @prop {boolean} withSquare
 * @prop {boolean} withSearch
 * @prop {*} query
 * @prop {*} onQueryChange
 * @return {*}
 */
export const EditableInformationTextSelect = ({
  label,
  options,
  defaultEditingState = false,
  canEdit = true,
  withSquare = true,
  withSearch = false,
  query,
  onQueryChange,
  ...props
}) => {
  const { t } = useTranslation();
  const [field, meta] = useField(props);
  const [editing, setEditing] = useState(defaultEditingState);

  const labelValue = options?.find((x) => x.value === field.value);

  return (
    <div className="EditableInformationTextInput">
      {!editing && (
        <EditableInformationStatusDisplay
          value={withSquare ? field.value : labelValue?.label}
          withSquare={withSquare}
        />
      )}
      {canEdit && editing && (
        <div className="EditableInformationTextInput__select">
          <Select
            change={(value) => {
              field.onChange({ target: { label, value, name: props.name } });
            }}
            value={field.value}
            type="editItem"
            withSearch={withSearch}
            query={query}
            onQueryChange={onQueryChange}
          >
            {options.map(({ label, value }) => (
              <SelectOption value={value} key={value}>
                {t(label)}
              </SelectOption>
            ))}
          </Select>
        </div>
      )}
      {meta.touched && meta.error ? (
        <div className="EditableInformationTextInput__error">
          <Tooltip
            padding={0}
            content={
              <PasswordErrorMessageTooltip>
                {t(meta.error)}
              </PasswordErrorMessageTooltip>
            }
          >
            <Icon name="iconNotification" width={20} height={20} />
          </Tooltip>
        </div>
      ) : null}
      {canEdit && !defaultEditingState && (
        <div className="EditableInformationTextInput__button">
          <div
            className="EditableInformationTextInput__button-toggle"
            onClick={() => setEditing(!editing)}
          >
            {!defaultEditingState && (
              <Icon
                name={editing ? "iconCheckmark" : "iconEdit"}
                width={15}
                height={18}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 *
 * @param {*} props
 * @prop {string} props.label
 * @prop {boolean} props.defaultEditingState
 * @prop {function} props.onBlur
 * @prop {boolean} props.canEdit
 * @return {*}
 */
export const EditableInformationCheckboxInput = ({
  label,
  defaultEditingState = false,
  onBlur = () => {},
  canEdit = true,
  ...props
}) => {
  const { t } = useTranslation();
  const [field, meta, helpers] = useField(props);
  const [editing] = useState(true);

  const onChangeHandler = () => {
    helpers.setValue(!field.value);
  };

  return (
    <div className="EditableInformationCheckboxInput">
      {canEdit && editing && (
        <div
          className="CustomCheckbox"
          onClick={onChangeHandler}
          onBlur={(args) => {
            field.onBlur(args);
            onBlur();
          }}
        >
          {field.checked && (
            <div className="CustomCheckbox__checked">
              <Icon name="iconCheckmark" width={11} height={11} />
            </div>
          )}
        </div>
      )}
      {label && (
        <label
          className={
            meta.value !== ""
              ? "EditableInformationCheckboxInput__label EditableInformationCheckboxInput__label--focused"
              : "EditableInformationCheckboxInput__label"
          }
        >
          {label}
        </label>
      )}
      {meta.touched && meta.error ? (
        <div className="EditableInformationTextInput__error">
          <Tooltip
            padding={0}
            content={
              <PasswordErrorMessageTooltip>
                {t(meta.error)}
              </PasswordErrorMessageTooltip>
            }
          >
            <Icon name="iconNotification" width={20} height={20} />
          </Tooltip>
        </div>
      ) : null}
    </div>
  );
};

export const EditableInformationItemWrapper = ({ label, children }) => {
  const { t } = useTranslation();
  return (
    <div className="EditableInformationDisplay EditableInformationDisplay--editing">
      <div className="EditableInformationDisplay__label">
        <div className="EditableInformationDisplay__label--text">
          {t(label)}
        </div>
      </div>
      {children}
    </div>
  );
};
